import { useEffect } from 'react';

// Shared styling for the public referral surfaces (/referral, /partner-signup,
// /partner-onboarding). These pages use a fixed dark-grey brand theme (hardcoded
// per branding, not the app's light/dark tokens): #38383B page background,
// #4A4A4E tile, black fields, white headings, lighter-grey body text.

export const PAGE_BG = '#38383B';

// The autofill: utilities defeat Chrome's white/light-blue paint on
// autofilled inputs (and the hover-a-suggestion preview state). The
// background can't be overridden directly, so the change is stalled with a
// near-infinite transition delay (the inset-shadow cover trick subtly
// mis-rounds the corners); -webkit-text-fill-color keeps the text white.
export const INPUT =
  'h-10 w-full rounded-md bg-black px-3 text-base text-white sm:text-sm placeholder:text-neutral-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary autofill:[transition:background-color_9999999s_ease-in-out_0s] autofill:[-webkit-text-fill-color:#fff] autofill:[caret-color:#fff]';

export const LABEL = 'mb-1 block text-xs font-medium text-neutral-300';

// Shared button: enabled = brighter fill + white text (clearly "live");
// disabled = flat dim fill + muted text. Deliberately distinct without going
// white (white is reserved for the hover state).
export const BUTTON =
  'inline-flex items-center justify-center gap-1.5 rounded-md bg-white/15 px-4 text-sm font-medium text-white transition-colors hover:bg-neutral-200 hover:!text-black disabled:pointer-events-none disabled:bg-white/5 disabled:text-neutral-500';

// react-select restyle to match the brand theme's black inputs, incl. the
// rounded floating-thumb scrollbar. NOTE: the standard scrollbar-width/color
// props are deliberately absent - Chrome 121+ ignores every
// ::-webkit-scrollbar-* style (incl. the arrow-button removal) when present.
export const SELECT_DARK = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#000',
    border: 'none',
    borderRadius: '0.375rem',
    minHeight: '2.5rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(255,255,255,0.35)' : 'none',
  }),
  singleValue: (base) => ({ ...base, color: '#fff' }),
  input: (base) => ({ ...base, color: '#fff' }),
  placeholder: (base) => ({ ...base, color: '#737373' }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#1c1c1e',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 10px 25px rgba(0,0,0,0.45)',
  }),
  menuList: (base) => ({
    ...base,
    padding: '0.25rem',
    '::-webkit-scrollbar': { width: '11px' },
    '::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
    // Inset the track so the thumb never rides into the rounded corners.
    '::-webkit-scrollbar-track': { background: 'transparent', margin: '8px 0' },
    // Transparent border + content-box clip = a floating thumb with a gap
    // from the menu edge (the scrollbar itself always hugs the edge).
    '::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.2)',
      borderRadius: '6px',
      border: '3px solid transparent',
      backgroundClip: 'content-box',
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255,255,255,0.3)',
      border: '3px solid transparent',
      backgroundClip: 'content-box',
    },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'rgba(255,255,255,0.12)' : 'transparent',
    color: '#fff',
    borderRadius: '0.5rem',
    cursor: 'pointer',
  }),
  groupHeading: (base) => ({
    ...base,
    color: '#a3a3a3',
    fontWeight: 600,
  }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: 'rgba(255,255,255,0.2)' }),
  dropdownIndicator: (base) => ({ ...base, color: '#a3a3a3' }),
  clearIndicator: (base) => ({ ...base, color: '#a3a3a3' }),
};

// Match the document body to the page background so no white shows below the
// layout at any viewport height. Restored on unmount so other pages aren't
// affected.
export const useBrandBodyBackground = () => {
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = PAGE_BG;
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);
};
